import { useQuery } from '@tanstack/react-query';
import type { IbhShape, IbhUnitSystem } from '@igus/iglidur-bearing-hub-api';
import type { Session } from '@hub/hooks/states/useSessionStore';
import type { IbhDimensions } from '@hub/bearing-hub/common/types';
import { Article } from '@hub/bearing-hub/ProductDetails/ArticleData/article';
import {
  type IbhCatalogArticleResponseDto,
  type IbhCustomArticleResponseDto,
  IbhProductionMethods,
} from '@igus/iglidur-bearing-hub-api/src/data-contracts';
import getConfig from 'next/config';
import type { NextRuntimeConfiguration } from '@hub/libs/config';
import { useHasDynamicPrice } from '@hub/hooks/useHasDynamicPrice';

const { publicRuntimeConfig } = getConfig() as NextRuntimeConfiguration;

interface UseArticleProps {
  articleNumber: string;
  isCustom: boolean;
  quantity: number;
  shapeKey: IbhShape;
  dimensions?: IbhDimensions;
  unitSystem: IbhUnitSystem;
  session: Session;
  initialArticleData: IbhCustomArticleResponseDto | IbhCatalogArticleResponseDto;
  useIcn: boolean;
}

export const useArticle = ({
  articleNumber,
  isCustom,
  quantity,
  shapeKey,
  session,
  unitSystem,
  dimensions,
  initialArticleData,
  useIcn,
}: UseArticleProps) => {
  const queryKey = [
    'article',
    articleNumber,
    isCustom,
    quantity,
    shapeKey,
    session?.currency,
    unitSystem,
    JSON.stringify(dimensions),
  ];

  const fetchArticleData = async () => {
    const article = Article({
      articleNumber,
      isCustom,
      quantity,
      shapeKey,
      session,
      unitSystem,
      dimensions,
      useIcn: useIcn && initialArticleData.productionMethod === IbhProductionMethods.SEMIFINISHED,
    });
    return article.fetchArticleData();
  };
  const hasDynamicPrices = useHasDynamicPrice();
  const enabled =
    (!!session && quantity !== initialArticleData.quantity) || (typeof window !== 'undefined' && hasDynamicPrices);

  const {
    data: articleData,
    isFetching,
    isLoading,
    error,
  } = useQuery({
    queryKey,
    queryFn: fetchArticleData,
    enabled,
    gcTime: publicRuntimeConfig.ibhUseQueryCacheTtl,
    placeholderData: initialArticleData,
    retry: failureCount => failureCount <= 1 && navigator.onLine,
  });

  return {
    article: articleData || initialArticleData,
    articleIsLoading: isLoading || isFetching,
    error,
  };
};
